import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGraduationCap, faSpinner, faVideo, faEnvelope, faCalendarAlt, faCheckCircle, faTimesCircle, faPlay, faBook } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError } from '@/utils/toasts';
import { formatDate } from '@/utils/helpers';
import type { Course, UserID, UserCourseProgressResponse, ModuleID, UserVideoProgress, Module } from '@/models';

interface UserCourseProgressSectionProps {
  userId: UserID;
  userName: string;
}

interface CourseProgressWithData extends UserCourseProgressResponse {
  course: Course;
  modules: Module[];
}

interface ModuleProgressData {
  moduleID: ModuleID;
  moduleName: string;
  totalVideos: number;
  watchedVideos: number;
  progressPercentage: number;
  videos: UserVideoProgress[];
}

export default function UserCourseProgressSection({ userId, userName }: UserCourseProgressSectionProps) {
  const [loading, setLoading] = useState(true);
  const [courseProgresses, setCourseProgresses] = useState<CourseProgressWithData[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCourseProgresses();
  }, [userId]);

  const fetchCourseProgresses = async () => {
    try {
      setLoading(true);
      setError(null);

      // First get all courses
      const coursesResponse = await APIRoutes.listCourses({ published: true });
      const progressData: CourseProgressWithData[] = [];

      // For each course, try to get user's progress
      for (const course of coursesResponse.courses) {
        try {
          const progress = await APIRoutes.getUserCourseProgress(userId, course.id);
          // Fetch modules for this course
          const modulesResponse = await APIRoutes.listModules(course.id, { published: true });
          progressData.push({
            ...progress,
            course,
            modules: modulesResponse.modules
          });
        } catch {
          // If user has no progress for this course, skip it
          console.log(`No progress found for course ${course.id}`);
        }
      }

      setCourseProgresses(progressData);
    } catch (err) {
      console.error('Error loading course progresses:', err);
      setError('Fehler beim Laden der Kurs-Fortschritte');
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">Lade Kurs-Fortschritte...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (courseProgresses.length === 0) {
    return (
      <div className="text-center py-8">
        <FontAwesomeIcon icon={faGraduationCap} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">
          {userName} hat noch keine Kurse begonnen
        </p>
      </div>
    );
  }

  // Helper function to calculate module progress from video progress
  const calculateModuleProgress = (videoProgress: UserVideoProgress[], modules: Module[]): ModuleProgressData[] => {
    const moduleMap = new Map<ModuleID, UserVideoProgress[]>();

    // Group videos by module
    videoProgress.forEach(vp => {
      if (!moduleMap.has(vp.moduleID)) {
        moduleMap.set(vp.moduleID, []);
      }
      moduleMap.get(vp.moduleID)!.push(vp);
    });

    // Calculate progress for each module
    // Backend returns progress as 0.0-1.0, videos are considered watched at >= 0.95 (95%)
    const moduleProgressArray: ModuleProgressData[] = [];
    moduleMap.forEach((videos, moduleID) => {
      const totalVideos = videos.length;
      const watchedVideos = videos.filter(v => v.progress >= 0.95).length;
      const progressPercentage = totalVideos > 0 ? Math.round((watchedVideos / totalVideos) * 100) : 0;

      // Get module name from modules array
      const module = modules.find(m => m.id === moduleID);
      const moduleName = module?.title ?? moduleID;

      moduleProgressArray.push({
        moduleID,
        moduleName,
        totalVideos,
        watchedVideos,
        progressPercentage,
        videos: videos.sort((a, b) => {
          const aIndex = a.video?.globalIndex ?? '';
          const bIndex = b.video?.globalIndex ?? '';
          return aIndex.localeCompare(bIndex, undefined, { numeric: true, sensitivity: 'base' });
        })
      });
    });

    // Sort by module order
    return moduleProgressArray.sort((a, b) => {
      const moduleA = modules.find(m => m.id === a.moduleID);
      const moduleB = modules.find(m => m.id === b.moduleID);
      return (moduleA?.order ?? 0) - (moduleB?.order ?? 0);
    });
  };

  return (
    <div className="space-y-6">
      {courseProgresses.map(({ course, courseProgress, videoProgress, sequenceTracker, modules }) => {
        const totalVideos = course.videoCount || 0;
        const watchedVideos = videoProgress.filter(vp => vp.progress >= 0.95).length;
        const progressPercentage = totalVideos > 0 ? Math.round((watchedVideos / totalVideos) * 100) : 0;
        const moduleProgress = calculateModuleProgress(videoProgress, modules);

        return (
          <div key={course.id} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <Link
                    to={`/admin/courses/${course.id}`}
                    className="text-lg font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    {course.title}
                  </Link>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                  {course.description}
                </p>

                <div className="flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-1" />
                    <span>
                      Gestartet: {courseProgress.startedAt ? formatDate(courseProgress.startedAt) : 'Nicht gestartet'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faVideo} className="w-4 h-4 mr-1" />
                    <span>{watchedVideos} von {totalVideos} Videos</span>
                  </div>
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4 mr-1" />
                    <span>{sequenceTracker.progress.length} E-Mails gesendet</span>
                  </div>
                </div>
              </div>

              <div className="text-right ml-4">
                <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  {progressPercentage}%
                </div>
                <div className="w-20 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Video Progress Table */}
            {videoProgress.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Video-Fortschritt</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Index
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Video
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Fortschritt
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Gestartet
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Beendet
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {videoProgress.map((vp) => {
                        const isCompleted = vp.progress >= 0.95;
                        return (
                          <tr key={vp.id}>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                              { vp.video?.globalIndex }
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                              <Link to={`/courses/${vp.courseID}/modules/${vp.moduleID}/videos/${vp.videoID}`}
                                    className="mr-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">{vp.video?.title ?? vp.videoID}</Link>
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <div className="flex items-center">
                                <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-2">
                                  <div
                                    className="bg-green-600 h-2 rounded-full"
                                    style={{ width: `${Math.round(vp.progress * 100)}%` }}
                                  />
                                </div>
                                <span className="text-gray-600 dark:text-gray-400">
                                  {Math.round(vp.progress * 100)}%
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                              {(vp.startedAt) ? formatDate(vp.startedAt) : 'Nicht gestartet'}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                                {(vp.finishedAt) ? formatDate(vp.finishedAt) : 'Nicht beendet'}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <div className="flex items-center">
                                <FontAwesomeIcon
                                  icon={isCompleted ? faCheckCircle : faPlay}
                                  className={`w-4 h-4 mr-1 ${
                                    isCompleted
                                      ? 'text-green-600 dark:text-green-400'
                                      : 'text-yellow-600 dark:text-yellow-400'
                                  }`}
                                />
                                <span className={`text-xs font-medium ${
                                  isCompleted
                                    ? 'text-green-600 dark:text-green-400'
                                    : 'text-yellow-600 dark:text-yellow-400'
                                }`}>
                                  {isCompleted ? 'Abgeschlossen' : 'In Bearbeitung'}
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Module Progress */}
            {moduleProgress.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Modul-Fortschritt</h4>
                <div className="grid gap-3">
                  {moduleProgress.map((module) => (
                    <div key={module.moduleID} className="bg-white dark:bg-gray-800 p-4 rounded border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center flex-1">
                          <FontAwesomeIcon icon={faBook} className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                          <Link
                            to={`/courses/${course.id}/modules/${module.moduleID}`}
                            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                          >
                            {module.moduleName}
                          </Link>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-lg font-bold text-gray-900 dark:text-white">
                            {module.progressPercentage}%
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-2">
                        <FontAwesomeIcon icon={faVideo} className="w-3 h-3 mr-1" />
                        <span>{module.watchedVideos} von {module.totalVideos} Videos abgeschlossen</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${module.progressPercentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mail Sequence Tracker */}
            {sequenceTracker.progress.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Mail-Sequenz Verlauf</h4>
                <div className="space-y-2">
                  {sequenceTracker.progress.map((entry, index) => (
                    <div key={index} className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={entry.action === 'SENT' ? faCheckCircle : faTimesCircle}
                          className={`w-4 h-4 mr-2 ${
                            entry.action === 'SENT'
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-orange-600 dark:text-orange-400'
                          }`}
                        />
                        <span className="text-sm text-gray-900 dark:text-white">
                          Eintrag: {entry.id}
                        </span>
                        <span className={`ml-3 text-xs px-2 py-1 rounded-full font-medium ${
                          entry.action === 'SENT'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                        }`}>
                          {entry.action === 'SENT' ? 'Gesendet' : 'Übersprungen'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(entry.timestamp)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}